import React, {
	HTMLAttributes,
	ReactElement,
	ReactNode,
	Ref,
	createContext,
	forwardRef,
	useCallback,
	useState,
} from 'react';
import { classNames } from 'primereact/utils';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import type { BoxSectionProps } from './BoxSection';
import './Box.scss';
import { Icon } from '../Icon';

type BoxVariant = 'white' | 'white-bordered' | 'white-rose-bordered' | 'blue' | 'blue-bordered' | 'clean' | 'darkblue';

export type BoxProps = Pick<HTMLAttributes<HTMLDivElement>, 'id' | 'className' | 'style' | 'onClick'> & {
	title?: string;
	slim?: boolean;
	variant?: BoxVariant;
	vertical?: boolean;
	full?: boolean;
	wrap?: boolean;
	// visible?: boolean;
	overflowHidden?: boolean;
	dropShadow?: boolean;
	hoverable?: boolean;
	noPad?: boolean;
	img?: string | (() => ReactNode);
	draggable?: boolean;
	dragHandleProps?: DraggableProvidedDragHandleProps | null;
	children: ReactNode | ReactElement<BoxSectionProps> | ReactElement<BoxSectionProps>[];
};

export const BoxLayoutContext = createContext<{ isVertical: boolean }>({ isVertical: false });

export const Box = forwardRef(
	(
		{
			title,
			slim,
			variant = 'white',
			vertical = false,
			full = false,
			wrap = false,
			children,
			className,
			// visible = false,
			overflowHidden = false,
			dropShadow = false,
			hoverable = false,
			noPad = false,
			img,
			dragHandleProps,
			...restProps
		}: BoxProps,
		ref: Ref<HTMLDivElement>
	) => {
		const [hoverActive, setHoverActive] = useState(false);
		const handleMouseHover = useCallback(() => setHoverActive(true), []);
		const handleMouseLeave = useCallback(() => setHoverActive(false), []);
		const bgImg = img === 'default' ? 'https://kinokrad.net/templates/kinokradcc/images/placeholder.png' : img;
		return (
			<BoxLayoutContext.Provider value={{ isVertical: vertical }}>
				<div
					ref={ref}
					className={classNames(
						'box',
						`box-variant-${variant}`,
						{
							'box-column': !vertical,
							'box-row': vertical,
							'box-slim': slim,
							'box-full': full,
							'box-wrap': wrap,
							// 'box-visible': visible,
							'box-hidden': overflowHidden,
							'box-shadow': dropShadow,
							'box-hover': restProps?.onClick || hoverable,
							'box-hover-active': hoverActive,
							'box-hoverable': hoverable,
							'box-nopad': noPad,
							'box-withimg': Boolean(img),
							'box-draggable': Boolean(dragHandleProps),
						},
						className
					)}
					onMouseEnter={handleMouseHover}
					onMouseLeave={handleMouseLeave}
					{...restProps}
				>
					{Boolean(dragHandleProps) && (
						<div className="box-drag bg-gray-100 text-gray-400" {...(dragHandleProps || {})}>
							<Icon name="up-long" />
							<Icon name="grip-lines" />
							<Icon name="down-long" />
						</div>
					)}
					{img && (
						<>
							{typeof img === 'string' && (
								<div
									className="box-img"
									style={{
										backgroundImage: `url(${bgImg})`,
									}}
								/>
							)}
							{typeof img === 'function' && <div className="box-img">{img()}</div>}
						</>
					)}
					{children}
				</div>
			</BoxLayoutContext.Provider>
		);
	}
);
